import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tooltip,
  Chip,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  LinearProgress,
  useTheme,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TimelineIcon from "@mui/icons-material/Timeline";
import EditNoteIcon from "@mui/icons-material/EditNote";

export default function MOSPhaseOverviewModal({
  open,
  onClose,
  salesOrders = [],
  onOpenPhaseModal,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [selectedPhaseName, setSelectedPhaseName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // ── Step 1: Real-time Grouping of all MOS orders phase-wise ──
  const phaseSummary = useMemo(() => {
    const map = {};

    salesOrders.forEach((order) => {
      const phases = order.mos_phases || [];
      phases.forEach((phase) => {
        const name = phase.phase_name || "Unknown Phase";
        if (!map[name]) {
          map[name] = {
            name,
            order_no: phase.order_no ?? 999,
            pending: [],
            inProgress: [],
            completed: [],
            total: 0,
          };
        }

        const rowData = {
          orderId: order.id,
          so_no: order.so_no,
          project_name: order.project_name || "N/A",
          debtor: order.party_details?.company_name || "N/A",
          delivery_date: order.delivery_date,
          order_date: order.order_date,
          grand_total: order.grand_total,
          so_status: order.status,
          phaseId: phase.id,
          phaseStatus: phase.status || "Pending",
          days: phase.days,
          completed_by_name: phase.completed_by_name,
          updated_at: phase.updated_at,
          fullOrder: order,
        };

        if (phase.status === "Completed") {
          map[name].completed.push(rowData);
        } else if (phase.status === "In Progress") {
          map[name].inProgress.push(rowData);
        } else {
          map[name].pending.push(rowData);
        }
        map[name].total += 1;
      });
    });

    return Object.values(map).sort((a, b) => a.order_no - b.order_no);
  }, [salesOrders]);

  // Set default active phase
  const activePhase = useMemo(() => {
    if (!phaseSummary.length) return null;
    if (selectedPhaseName) {
      const found = phaseSummary.find((p) => p.name === selectedPhaseName);
      if (found) return found;
    }
    return phaseSummary[0];
  }, [phaseSummary, selectedPhaseName]);

  // Filter items by search
  const filterList = (items) => {
    if (!searchTerm.trim()) return items;
    const q = searchTerm.toLowerCase();
    return items.filter(
      (item) =>
        item.so_no?.toLowerCase().includes(q) ||
        item.project_name?.toLowerCase().includes(q) ||
        item.debtor?.toLowerCase().includes(q)
    );
  };

  const pendingItems = filterList(activePhase?.pending || []);
  const inProgressItems = filterList(activePhase?.inProgress || []);
  const completedItems = filterList(activePhase?.completed || []);

  const totalInActivePhase = activePhase ? activePhase.total : 0;
  const completedInActivePhase = activePhase ? activePhase.completed.length : 0;
  const phaseProgress = totalInActivePhase
    ? Math.round((completedInActivePhase / totalInActivePhase) * 100)
    : 0;

  // Solid theme colors
  const tableHeadBg = isDark ? "#252b38" : "#f1f5f9";
  const rowEvenBg = isDark ? "#1e222d" : "#ffffff";
  const rowOddBg = isDark ? "#191c24" : "#fbfcfd";
  const hoverBg = isDark ? "#2a3040" : "#eef4ff";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";

  const truncate = (str, n = 22) =>
    str && str.length > n ? str.substring(0, n) + "…" : str || "—";

  // ── Reusable Compact Status Table ──
  const renderStatusTable = (title, count, items, statusType, headerColor, icon) => {
    return (
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          border: `1px solid ${borderColor}`,
          overflow: "hidden",
          bgcolor: isDark ? "#1a1d26" : "#ffffff",
        }}
      >
        {/* Column Header */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: isDark ? "#20242e" : "#f8f9fa",
            borderBottom: `2.5px solid ${headerColor}`,
          }}
        >
          <Box display="flex" alignItems="center" gap={0.8}>
            {icon}
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: "0.82rem" }}>
              {title}
            </Typography>
          </Box>
          <Chip
            label={count}
            size="small"
            sx={{
              height: 20,
              minWidth: 26,
              fontSize: "0.7rem",
              fontWeight: 700,
              bgcolor: headerColor,
              color: "#ffffff",
              borderRadius: 1,
            }}
          />
        </Box>

        {/* Scrollable Table Container */}
        <TableContainer
          sx={{
            flex: 1,
            maxHeight: 460,
            minHeight: 380,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 5 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: isDark ? "#333b4d" : "#cbd5e1",
              borderRadius: 4,
            },
          }}
        >
          <Table size="small" stickyHeader sx={{ minWidth: 260 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: 28,
                    p: "4px 6px",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    bgcolor: `${tableHeadBg} !important`,
                    borderBottom: `1px solid ${borderColor}`,
                  }}
                  align="center"
                >
                  #
                </TableCell>
                <TableCell
                  sx={{
                    width: 75,
                    p: "4px 6px",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    bgcolor: `${tableHeadBg} !important`,
                    borderBottom: `1px solid ${borderColor}`,
                  }}
                >
                  SO No.
                </TableCell>
                <TableCell
                  sx={{
                    p: "4px 6px",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    bgcolor: `${tableHeadBg} !important`,
                    borderBottom: `1px solid ${borderColor}`,
                  }}
                >
                  Debtor & Project
                </TableCell>
                <TableCell
                  sx={{
                    width: 85,
                    p: "4px 6px",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    bgcolor: `${tableHeadBg} !important`,
                    borderBottom: `1px solid ${borderColor}`,
                  }}
                  align="center"
                >
                  {statusType === "Completed" ? "Done By" : "Delivery"}
                </TableCell>
                <TableCell
                  sx={{
                    width: 32,
                    p: "4px 4px",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    bgcolor: `${tableHeadBg} !important`,
                    borderBottom: `1px solid ${borderColor}`,
                  }}
                  align="center"
                >
                  Act
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{
                      py: 6,
                      fontSize: "0.75rem",
                      color: "text.disabled",
                      borderBottom: "none",
                    }}
                  >
                    No {title} MOS
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row, idx) => (
                  <TableRow
                    key={row.orderId + "-" + row.phaseId}
                    sx={{
                      bgcolor: idx % 2 === 0 ? rowEvenBg : rowOddBg,
                      "&:hover": { bgcolor: `${hoverBg} !important` },
                      "& td": {
                        borderBottom: `1px solid ${borderColor}`,
                        p: "5px 6px",
                      },
                    }}
                  >
                    {/* Index */}
                    <TableCell align="center" sx={{ fontSize: "0.68rem", color: "text.secondary" }}>
                      {idx + 1}
                    </TableCell>

                    {/* SO No */}
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: "primary.main",
                          lineHeight: 1.2,
                        }}
                      >
                        {row.so_no}
                      </Typography>
                    </TableCell>

                    {/* Debtor & Project (Compact 2 Lines) */}
                    <TableCell>
                      <Tooltip title={`Party: ${row.debtor}`} arrow placement="top">
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            color: "text.primary",
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 150,
                          }}
                        >
                          {truncate(row.debtor, 20)}
                        </Typography>
                      </Tooltip>
                      <Tooltip title={`MOS: ${row.project_name}`} arrow placement="bottom">
                        <Typography
                          sx={{
                            fontSize: "0.65rem",
                            color: "text.secondary",
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 150,
                          }}
                        >
                          {truncate(row.project_name, 22)}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Delivery Date or Completed By */}
                    <TableCell align="center">
                      {statusType === "Completed" ? (
                        row.completed_by_name ? (
                          <Tooltip title={`Completed by ${row.completed_by_name}`} arrow>
                            <Chip
                              label={row.completed_by_name.split(" ")[0]}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.62rem",
                                bgcolor: isDark ? alpha("#2e7d32", 0.25) : "#e8f5e9",
                                color: isDark ? "#81c784" : "#2e7d32",
                                fontWeight: 700,
                                px: 0.5,
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography sx={{ fontSize: "0.65rem", color: "text.disabled" }}>—</Typography>
                        )
                      ) : (
                        <Typography
                          sx={{
                            fontSize: "0.68rem",
                            color: "text.secondary",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.delivery_date
                            ? new Date(row.delivery_date).toLocaleDateString("en-GB")
                            : "—"}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Action */}
                    <TableCell align="center" sx={{ p: "2px 4px !important" }}>
                      <Tooltip title="Update MOS Phase" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onOpenPhaseModal?.(row.fullOrder)}
                          sx={{
                            p: 0.4,
                            borderRadius: 1,
                            bgcolor: isDark ? alpha(theme.palette.primary.main, 0.15) : "#eef4ff",
                            color: "primary.main",
                            "&:hover": {
                              bgcolor: "primary.main",
                              color: "#fff",
                            },
                          }}
                        >
                          <EditNoteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          bgcolor: "background.paper",
          backgroundImage: "none",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: isDark
            ? "0 20px 60px rgba(0,0,0,0.75)"
            : "0 16px 48px rgba(0,0,0,0.15)",
          maxHeight: "92vh",
        },
      }}
    >
      {/* ── HEADER ── */}
      <DialogTitle
        sx={{
          p: 2,
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          {/* Title */}
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                p: 0.8,
                borderRadius: 1.5,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                color: "primary.main",
                display: "flex",
                alignItems: "center",
              }}
            >
              <TimelineIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                MOS Phase Tracking & Summary
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select a phase tab to see Pending, WIP and Completed orders in high-density table view
              </Typography>
            </Box>
          </Box>

          {/* Search + Close */}
          <Box display="flex" alignItems="center" gap={1.2}>
            <TextField
              size="small"
              placeholder="Search SO / Debtor / MOS..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: 160, sm: 240 },
                "& .MuiInputBase-root": {
                  height: 32,
                  fontSize: "0.75rem",
                  borderRadius: 1,
                },
              }}
            />

            <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* ── CLEAN PHASE TABS (No pending/wip/done badges on tabs) ── */}
        <Box sx={{ mt: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activePhase?.name || false}
            onChange={(e, newName) => setSelectedPhaseName(newName)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 38,
              "& .MuiTab-root": {
                minHeight: 38,
                py: 0.5,
                px: 1.8,
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 600,
                letterSpacing: "0.02em",
              },
            }}
          >
            {phaseSummary.map((phase) => (
              <Tab
                key={phase.name}
                value={phase.name}
                label={`${phase.name} (${phase.total})`}
              />
            ))}
          </Tabs>
        </Box>

        {/* ── Active Phase Progress Bar ── */}
        {activePhase && (
          <Box display="flex" alignItems="center" gap={2} mt={1.2} px={0.5}>
            <Typography variant="caption" sx={{ fontSize: "0.72rem", fontWeight: 600, color: "text.secondary" }}>
              Phase Completion:
            </Typography>
            <Box sx={{ flex: 1, maxWidth: 220 }}>
              <LinearProgress
                variant="determinate"
                value={phaseProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: phaseProgress === 100 ? "#2e7d32" : "primary.main",
                    borderRadius: 3,
                  },
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ fontSize: "0.72rem", fontWeight: 700, color: phaseProgress === 100 ? "success.main" : "primary.main" }}>
              {phaseProgress}% ({completedInActivePhase}/{totalInActivePhase} Completed)
            </Typography>
          </Box>
        )}
      </DialogTitle>

      {/* ── 3-COLUMN TABLE LAYOUT ── */}
      <DialogContent
        sx={{
          p: 2,
          bgcolor: isDark ? "#12151c" : "#f4f6fa",
          overflowY: "auto",
        }}
      >
        {!activePhase ? (
          <Box textAlign="center" py={8}>
            <Typography color="text.secondary" variant="body2">
              No MOS Phase data found for the selected filter.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1.8}>
            {/* 1️⃣ PENDING TABLE */}
            <Grid item xs={12} md={4}>
              {renderStatusTable(
                "Pending",
                pendingItems.length,
                pendingItems,
                "Pending",
                "#9e9e9e",
                <HourglassEmptyIcon sx={{ fontSize: 17, color: "#9e9e9e" }} />
              )}
            </Grid>

            {/* 2️⃣ IN PROGRESS TABLE */}
            <Grid item xs={12} md={4}>
              {renderStatusTable(
                "In Progress",
                inProgressItems.length,
                inProgressItems,
                "In Progress",
                "#ed6c02",
                <AutorenewIcon sx={{ fontSize: 17, color: "#ed6c02" }} />
              )}
            </Grid>

            {/* 3️⃣ COMPLETED TABLE */}
            <Grid item xs={12} md={4}>
              {renderStatusTable(
                "Completed",
                completedItems.length,
                completedItems,
                "Completed",
                "#2e7d32",
                <CheckCircleIcon sx={{ fontSize: 17, color: "#2e7d32" }} />
              )}
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
}